import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppContext from 'context/Context';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Accordion, Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { useToast } from 'context/providers/ToastProvider';
import FormatoEditable from './FormatoEditable';
import LlamadaAccionEditable from './LlamadaAccionEditable';

dayjs.locale('es');

const PublicacionEventModal = ({
    setIsOpenEventModal,
    isOpenEventModal,
    modalEventContent,
    onEstadoActualizado,
    selectedCuentaId
}) => {
    const {
        config: { isDark }
    } = useContext(AppContext);
    const { token } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    
    const [actualizandoEstado, setActualizandoEstado] = useState(null);
    const [actualizandoFecha, setActualizandoFecha] = useState(null);
    const [actualizandoFormato, setActualizandoFormato] = useState(null);
    const [actualizandoLlamadaAccion, setActualizandoLlamadaAccion] = useState(null);
    const [actualizandoDescripcion, setActualizandoDescripcion] = useState(null);
    const [actualizandoTema, setActualizandoTema] = useState(null);
    const [editandoFecha, setEditandoFecha] = useState(null);
    const [editandoFormato, setEditandoFormato] = useState(null);
    const [editandoLlamadaAccion, setEditandoLlamadaAccion] = useState(null);
    const [editandoDescripcion, setEditandoDescripcion] = useState(null);
    const [editandoTema, setEditandoTema] = useState(null);
    const [nuevaFecha, setNuevaFecha] = useState('');
    const [nuevoFormato, setNuevoFormato] = useState('');
    const [nuevaLlamadaAccion, setNuevaLlamadaAccion] = useState('');
    const [nuevaDescripcion, setNuevaDescripcion] = useState('');
    const [nuevoTema, setNuevoTema] = useState('');
    const [historial, setHistorial] = useState([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(false);
    const [publicacionActual, setPublicacionActual] = useState(null);
    const [fechaEvento, setFechaEvento] = useState(null);
    const [catalogoFormatos, setCatalogoFormatos] = useState([]);
    const [catalogoLlamadasAccion, setCatalogoLlamadasAccion] = useState([]);
    
    // Cargar datos iniciales cuando se abre el modal
    useEffect(() => {
        if (isOpenEventModal && modalEventContent.event) {
            const { start } = modalEventContent.event;
            const { publicacion, redesSociales, estado, nombreUsuario, tema, Formato, llamadaAccion } = modalEventContent.event.extendedProps;
            
            setFechaEvento(start);
            setPublicacionActual(publicacion ? {
                ...publicacion,
                redesSociales: redesSociales || publicacion.redesSociales || [],
                estado: estado || publicacion.estado || 'Planeado',
                nombreUsuario: nombreUsuario || publicacion.nombreUsuario,
                tema: tema || publicacion.tema || '',
                formato: Formato || publicacion.formato || null,
                llamadaAccion: llamadaAccion || publicacion.llamadaAccion || null
            } : null);

            if (publicacion && publicacion.idPublicacion) {
                cargarHistorial(publicacion.idPublicacion);
            }
            
            // Cargar catálogos
            cargarCatalogos();
        }
    }, [isOpenEventModal, modalEventContent]);

    const cargarCatalogos = async () => {
        try {
            const response = await fetch('/api/calendarioeditorial/ObtenerCatalogos', {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                setCatalogoFormatos(Array.from(detalles.formatos || []));
                setCatalogoLlamadasAccion(Array.from(detalles.llamadasAccion || []));
            }
        } catch (error) {
            console.error('Error al cargar catálogos:', error);
        }
    };

    const handleClose = () => {
        setIsOpenEventModal(!isOpenEventModal);
        setEditandoFecha(null);
        setEditandoFormato(null);
        setEditandoLlamadaAccion(null);
        setEditandoDescripcion(null);
        setEditandoTema(null);
        setNuevaFecha('');
        setNuevoFormato('');
        setNuevaLlamadaAccion('');
        setNuevaDescripcion('');
        setNuevoTema('');
        setPublicacionActual(null);
        setFechaEvento(null);
    };

    const redesSocialesInfo = {
        facebook: { nombre: 'Facebook', icon: ['fab', 'facebook'], color: '#1877F2' },
        tiktok: { nombre: 'TikTok', icon: ['fab', 'tiktok'], color: '#000000' },
        youtube: { nombre: 'YouTube', icon: ['fab', 'youtube'], color: '#FF0000' },
        instagram: { nombre: 'Instagram', icon: ['fab', 'instagram'], color: '#E4405F' },
        linkedin: { nombre: 'LinkedIn', icon: ['fab', 'linkedin'], color: '#0A66C2' },
        threads: { nombre: 'Threads', icon: ['fab', 'threads'], color: '#000000' },
        x: { nombre: 'X (Twitter)', icon: ['fab', 'x-twitter'], color: '#000000' }
    };

    const estadosConfig = {
        'Planeado': { color: '#009bdb', icon: 'lightbulb', id: 1 },
        'Programado': { color: '#fc9646', icon: 'clock', id: 2 },
        'Publicado': { color: '#89bd00', icon: 'check-circle', id: 3 }
    };

    const getEstadoConfig = (estado) => {
        return estadosConfig[estado] || { color: '#6c757d', icon: 'circle', id: 1 };
    };

    // Cargar historial cuando se abre el modal
    useEffect(() => {
        if (isOpenEventModal && modalEventContent.event) {
            const { publicacion } = modalEventContent.event.extendedProps;
            if (publicacion && publicacion.idPublicacion) {
                cargarHistorial(publicacion.idPublicacion);
            }
        }
    }, [isOpenEventModal, modalEventContent]);

    const cargarHistorial = async (idPublicacion) => {
        setCargandoHistorial(true);
        try {
            const response = await fetch(`/api/calendarioeditorial/obtenerhistorial?idPublicacion=${idPublicacion}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.iResponseType === 1 && data.detalles) {
                const detalles = JSON.parse(data.detalles);
                const historialData = detalles.table1 || [];
                setHistorial(historialData);
            } else {
                setHistorial([]);
            }
        } catch (error) {
            console.error('Error al cargar historial:', error);
            setHistorial([]);
        } finally {
            setCargandoHistorial(false);
        }
    };

    const handleCambiarEstado = async (publicacion, nuevoEstado) => {
        setActualizandoEstado(publicacion.idPublicacion);

        try {
            const estadoConfig = estadosConfig[nuevoEstado];

            // Obtener la fecha de la publicación desde el estado local
            const fechaPublicacion = fechaEvento;

            const requestData = {
                idPublicacion: publicacion.idPublicacion,
                descripcion: publicacion.descripcion,
                fechaPublicacion: dayjs(fechaPublicacion).format('YYYY-MM-DD'),
                redesSociales: publicacion.redesSociales || [],
                responsable: publicacion.nombreUsuario,
                idCatEstadoPublicacion: estadoConfig.id,
                tema: publicacion.tema || '',
                idCatFormatoPublicacion: publicacion.idCatFormatoPublicacion || 0,
                idCatLlamadaAccion: publicacion.idCatLlamadaAccion || 0,
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/guardarpublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast(`Estado actualizado a "${nuevoEstado}" exitosamente`);

                // Actualizar el estado local inmediatamente
                setPublicacionActual(prev => ({
                    ...prev,
                    estado: nuevoEstado
                }));

                // Recargar historial
                cargarHistorial(publicacion.idPublicacion);

                // Notificar al componente padre para recargar los datos del calendario
                if (onEstadoActualizado) {
                    setTimeout(() => {
                        onEstadoActualizado();
                    }, 500);
                }
            } else {
                showErrorToast(data.vMessage || 'Error al actualizar el estado');
            }
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            showErrorToast('Error al actualizar el estado');
        } finally {
            setActualizandoEstado(null);
        }
    };

    const handleCambiarFecha = async (publicacion) => {
        if (!nuevaFecha) {
            showErrorToast('Por favor selecciona una fecha válida');
            return;
        }

        setActualizandoFecha(publicacion.idPublicacion);

        try {
            const estadoConfig = estadosConfig[publicacion.estado || 'Planeado'];

            const requestData = {
                idPublicacion: publicacion.idPublicacion,
                descripcion: publicacion.descripcion,
                fechaPublicacion: dayjs(nuevaFecha).format('YYYY-MM-DD'),
                redesSociales: publicacion.redesSociales || [],
                responsable: publicacion.nombreUsuario,
                idCatEstadoPublicacion: estadoConfig.id,
                tema: publicacion.tema || '',
                idCatFormatoPublicacion: publicacion.idCatFormatoPublicacion || 0,
                idCatLlamadaAccion: publicacion.idCatLlamadaAccion || 0,
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/guardarpublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast('Fecha actualizada exitosamente');

                // Actualizar la fecha en el estado local inmediatamente
                setFechaEvento(new Date(nuevaFecha));
                
                setEditandoFecha(null);
                setNuevaFecha('');

                // Recargar historial
                cargarHistorial(publicacion.idPublicacion);

                // Notificar al componente padre para recargar los datos del calendario
                if (onEstadoActualizado) {
                    setTimeout(() => {
                        onEstadoActualizado();
                    }, 500);
                }
            } else {
                showErrorToast(data.vMessage || 'Error al actualizar la fecha');
            }
        } catch (error) {
            console.error('Error al cambiar fecha:', error);
            showErrorToast('Error al actualizar la fecha');
        } finally {
            setActualizandoFecha(null);
        }
    };

    const handleCambiarFormato = async (publicacion) => {
        if (!nuevoFormato) {
            showErrorToast('Por favor selecciona un formato válido');
            return;
        }

        setActualizandoFormato(publicacion.idPublicacion);

        try {
            const estadoConfig = estadosConfig[publicacion.estado || 'Planeado'];
            
            // Buscar el nombre del formato seleccionado
            const formatoSeleccionado = catalogoFormatos.find(f => f.idCatFormatoPublicacion === parseInt(nuevoFormato));

            const requestData = {
                idPublicacion: publicacion.idPublicacion,
                descripcion: publicacion.descripcion,
                fechaPublicacion: dayjs(fechaEvento).format('YYYY-MM-DD'),
                redesSociales: publicacion.redesSociales || [],
                responsable: publicacion.nombreUsuario,
                idCatEstadoPublicacion: estadoConfig.id,
                tema: publicacion.tema || '',
                idCatFormatoPublicacion: parseInt(nuevoFormato),
                idCatLlamadaAccion: publicacion.idCatLlamadaAccion || 0,
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/guardarpublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast('Formato actualizado exitosamente');

                // Actualizar el formato en el estado local inmediatamente
                setPublicacionActual(prev => ({
                    ...prev,
                    idCatFormatoPublicacion: parseInt(nuevoFormato),
                    formato: formatoSeleccionado ? formatoSeleccionado.vNombreFormato : null
                }));
                
                setEditandoFormato(null);
                setNuevoFormato('');

                // Recargar historial
                cargarHistorial(publicacion.idPublicacion);

                // Notificar al componente padre para recargar los datos del calendario
                if (onEstadoActualizado) {
                    setTimeout(() => {
                        onEstadoActualizado();
                    }, 500);
                }
            } else {
                showErrorToast(data.vMessage || 'Error al actualizar el formato');
            }
        } catch (error) {
            console.error('Error al cambiar formato:', error);
            showErrorToast('Error al actualizar el formato');
        } finally {
            setActualizandoFormato(null);
        }
    };

    const handleCambiarLlamadaAccion = async (publicacion) => {
        if (!nuevaLlamadaAccion) {
            showErrorToast('Por favor selecciona una llamada de acción válida');
            return;
        }

        setActualizandoLlamadaAccion(publicacion.idPublicacion);

        try {
            const estadoConfig = estadosConfig[publicacion.estado || 'Planeado'];
            
            // Buscar el nombre de la llamada de acción seleccionada
            const llamadaSeleccionada = catalogoLlamadasAccion.find(l => l.idCatLlamadaAccion === parseInt(nuevaLlamadaAccion));

            const requestData = {
                idPublicacion: publicacion.idPublicacion,
                descripcion: publicacion.descripcion,
                fechaPublicacion: dayjs(fechaEvento).format('YYYY-MM-DD'),
                redesSociales: publicacion.redesSociales || [],
                responsable: publicacion.nombreUsuario,
                idCatEstadoPublicacion: estadoConfig.id,
                tema: publicacion.tema || '',
                idCatFormatoPublicacion: publicacion.idCatFormatoPublicacion || 0,
                idCatLlamadaAccion: parseInt(nuevaLlamadaAccion),
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/guardarpublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast('Llamada de acción actualizada exitosamente');

                // Actualizar la llamada de acción en el estado local inmediatamente
                setPublicacionActual(prev => ({
                    ...prev,
                    idCatLlamadaAccion: parseInt(nuevaLlamadaAccion),
                    llamadaAccion: llamadaSeleccionada ? llamadaSeleccionada.vNombreLlamadaAccion : null
                }));
                
                setEditandoLlamadaAccion(null);
                setNuevaLlamadaAccion('');

                // Recargar historial
                cargarHistorial(publicacion.idPublicacion);

                // Notificar al componente padre para recargar los datos del calendario
                if (onEstadoActualizado) {
                    setTimeout(() => {
                        onEstadoActualizado();
                    }, 500);
                }
            } else {
                showErrorToast(data.vMessage || 'Error al actualizar la llamada de acción');
            }
        } catch (error) {
            console.error('Error al cambiar llamada de acción:', error);
            showErrorToast('Error al actualizar la llamada de acción');
        } finally {
            setActualizandoLlamadaAccion(null);
        }
    };

    const iniciarEdicionFecha = (publicacion) => {
        setEditandoFecha(publicacion.idPublicacion);
        setNuevaFecha(dayjs(fechaEvento).format('YYYY-MM-DD'));
    };

    const cancelarEdicionFecha = () => {
        setEditandoFecha(null);
        setNuevaFecha('');
    };

    const iniciarEdicionFormato = (publicacion) => {
        setEditandoFormato(publicacion.idPublicacion);
        setNuevoFormato(publicacion.idCatFormatoPublicacion ? publicacion.idCatFormatoPublicacion.toString() : '');
    };

    const cancelarEdicionFormato = () => {
        setEditandoFormato(null);
        setNuevoFormato('');
    };

    const iniciarEdicionLlamadaAccion = (publicacion) => {
        setEditandoLlamadaAccion(publicacion.idPublicacion);
        setNuevaLlamadaAccion(publicacion.idCatLlamadaAccion ? publicacion.idCatLlamadaAccion.toString() : '');
    };

    const cancelarEdicionLlamadaAccion = () => {
        setEditandoLlamadaAccion(null);
        setNuevaLlamadaAccion('');
    };

    const iniciarEdicionDescripcion = (publicacion) => {
        setEditandoDescripcion(publicacion.idPublicacion);
        setNuevaDescripcion(publicacion.descripcion);
    };

    const cancelarEdicionDescripcion = () => {
        setEditandoDescripcion(null);
        setNuevaDescripcion('');
    };

    const iniciarEdicionTema = (publicacion) => {
        setEditandoTema(publicacion.idPublicacion);
        setNuevoTema(publicacion.tema || '');
    };

    const cancelarEdicionTema = () => {
        setEditandoTema(null);
        setNuevoTema('');
    };

    const handleCambiarDescripcion = async (publicacion) => {
        if (!nuevaDescripcion.trim()) {
            showErrorToast('La descripción no puede estar vacía');
            return;
        }

        if (nuevaDescripcion.length > 500) {
            showErrorToast('La descripción no puede exceder 500 caracteres');
            return;
        }

        setActualizandoDescripcion(publicacion.idPublicacion);

        try {
            const estadoConfig = estadosConfig[publicacion.estado || 'Planeado'];

            const requestData = {
                idPublicacion: publicacion.idPublicacion,
                descripcion: nuevaDescripcion,
                fechaPublicacion: dayjs(fechaEvento).format('YYYY-MM-DD'),
                redesSociales: publicacion.redesSociales || [],
                responsable: publicacion.nombreUsuario,
                idCatEstadoPublicacion: estadoConfig.id,
                tema: publicacion.tema || '',
                idCatFormatoPublicacion: publicacion.idCatFormatoPublicacion || 0,
                idCatLlamadaAccion: publicacion.idCatLlamadaAccion || 0,
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/guardarpublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast('Descripción actualizada exitosamente');

                // Actualizar el estado local inmediatamente
                setPublicacionActual(prev => ({
                    ...prev,
                    descripcion: nuevaDescripcion
                }));
                
                setEditandoDescripcion(null);
                setNuevaDescripcion('');

                // Recargar historial
                cargarHistorial(publicacion.idPublicacion);

                // Notificar al componente padre para recargar los datos del calendario
                if (onEstadoActualizado) {
                    setTimeout(() => {
                        onEstadoActualizado();
                    }, 500);
                }
            } else {
                showErrorToast(data.vMessage || 'Error al actualizar la descripción');
            }
        } catch (error) {
            console.error('Error al cambiar descripción:', error);
            showErrorToast('Error al actualizar la descripción');
        } finally {
            setActualizandoDescripcion(null);
        }
    };

    const handleCambiarTema = async (publicacion) => {
        if (!nuevoTema.trim()) {
            showErrorToast('El tema no puede estar vacío');
            return;
        }

        setActualizandoTema(publicacion.idPublicacion);

        try {
            const estadoConfig = estadosConfig[publicacion.estado || 'Planeado'];

            const requestData = {
                idPublicacion: publicacion.idPublicacion,
                descripcion: publicacion.descripcion,
                fechaPublicacion: dayjs(fechaEvento).format('YYYY-MM-DD'),
                redesSociales: publicacion.redesSociales || [],
                responsable: publicacion.nombreUsuario,
                idCatEstadoPublicacion: estadoConfig.id,
                tema: nuevoTema,
                idCatFormatoPublicacion: publicacion.idCatFormatoPublicacion || 0,
                idCatLlamadaAccion: publicacion.idCatLlamadaAccion || 0,
                idCatCuentaComercial: selectedCuentaId
            };

            const response = await fetch('/api/calendarioeditorial/guardarpublicacion', {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();

            if (data.iResponseType === 1) {
                showSuccessToast('Tema actualizado exitosamente');

                // Actualizar el estado local inmediatamente
                setPublicacionActual(prev => ({
                    ...prev,
                    tema: nuevoTema
                }));
                
                setEditandoTema(null);
                setNuevoTema('');

                // Recargar historial
                cargarHistorial(publicacion.idPublicacion);

                // Notificar al componente padre para recargar los datos del calendario
                if (onEstadoActualizado) {
                    setTimeout(() => {
                        onEstadoActualizado();
                    }, 500);
                }
            } else {
                showErrorToast(data.vMessage || 'Error al actualizar el tema');
            }
        } catch (error) {
            console.error('Error al cambiar tema:', error);
            showErrorToast('Error al actualizar el tema');
        } finally {
            setActualizandoTema(null);
        }
    };

    if (!isOpenEventModal || !modalEventContent.event || !publicacionActual || !fechaEvento) {
        return null;
    }

    // Convertir publicacion individual en array para reutilizar el código existente
    const publicaciones = [publicacionActual];

    return (
        <>
            <Modal
                show={isOpenEventModal}
                onHide={handleClose}
                contentClassName="border-0"
                dialogClassName="publicacion-modal"
                centered
                size="lg"
            >
                <Modal.Header
                    closeButton
                    closeVariant={isDark ? 'white' : null}
                    className="border-bottom-0 py-4"
                    style={{
                        backgroundColor: isDark ? '#1a1d20' : '#ffffff',
                        borderBottom: `3px solid ${isDark ? '#343a40' : '#e9ecef'}`
                    }}
                >
                    <div className="w-100">
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                backgroundColor: isDark ? '#0d6efd' : '#e7f3ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <FontAwesomeIcon
                                    icon="calendar-check"
                                    style={{
                                        fontSize: '1.5rem',
                                        color: isDark ? 'white' : '#0d6efd'
                                    }}
                                />
                            </div>
                            <div className="flex-grow-1">
                                <h5 className="mb-0 fw-bold" style={{ fontSize: '1.25rem' }}>
                                    Detalles de Publicaci&oacute;n
                            </h5>
                            </div>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body
                    className="p-4"
                    style={{ backgroundColor: isDark ? '#212529' : '#f8f9fa' }}
                >
                    {publicaciones && publicaciones.length > 0 ? (
                        <div>
                            <small className="text-muted fw-semibold mb-3 d-block">
                                {publicaciones.length} {publicaciones.length === 1 ? 'publicaci\u00F3n' : 'publicaciones'}
                            </small>

                            {publicaciones.map((pub, index) => {
                                const redes = pub.redesSociales || [];
                                const estadoConfig = getEstadoConfig(pub.estado || 'Planeado');
                                const estaActualizando = actualizandoEstado === pub.idPublicacion;
                                const estaActualizandoFecha = actualizandoFecha === pub.idPublicacion;
                                const estaActualizandoFormato = actualizandoFormato === pub.idPublicacion;
                                const estaActualizandoLlamadaAccion = actualizandoLlamadaAccion === pub.idPublicacion;
                                const estaActualizandoDescripcion = actualizandoDescripcion === pub.idPublicacion;
                                const estaActualizandoTema = actualizandoTema === pub.idPublicacion;
                                const estaEditandoFecha = editandoFecha === pub.idPublicacion;
                                const estaEditandoFormato = editandoFormato === pub.idPublicacion;
                                const estaEditandoLlamadaAccion = editandoLlamadaAccion === pub.idPublicacion;
                                const estaEditandoDescripcion = editandoDescripcion === pub.idPublicacion;
                                const estaEditandoTema = editandoTema === pub.idPublicacion;

                                return (
                                    <div key={index}>
                                        <div
                                            className="border rounded p-3 mb-3"
                                            style={{
                                                backgroundColor: isDark ? '#2a2d31' : 'white',
                                                opacity: estaActualizando || estaActualizandoFecha || estaActualizandoFormato || estaActualizandoLlamadaAccion || estaActualizandoDescripcion || estaActualizandoTema ? 0.6 : 1,
                                                transition: 'opacity 0.3s',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            {/* Header con estado, formato y responsable */}
                                            <div className="d-flex align-items-start justify-content-between mb-3 pb-3 border-bottom">
                                                <div className="d-flex flex-wrap align-items-center gap-2">
                                                    <Dropdown>
                                                        <Dropdown.Toggle
                                                            variant=""
                                                            size="sm"
                                                            disabled={estaActualizando}
                                                            style={{
                                                                backgroundColor: estadoConfig.color,
                                                                color: 'white',
                                                                fontSize: '0.8rem',
                                                                padding: '0.4rem 0.75rem',
                                                                border: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '0.35rem',
                                                                fontWeight: '600',
                                                                borderRadius: '6px'
                                                            }}
                                                        >
                                                            {estaActualizando ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                                    Actualizando...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FontAwesomeIcon icon={estadoConfig.icon} />
                                                                    {pub.estado || 'Planeado'}
                                                                    <FontAwesomeIcon icon="chevron-down" className="ms-1" style={{ fontSize: '0.7rem' }} />
                                                                </>
                                                            )}
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu>
                                                            <Dropdown.Header>Cambiar estado a:</Dropdown.Header>
                                                            {Object.entries(estadosConfig).map(([nombreEstado, config]) => (
                                                                <Dropdown.Item
                                                                    key={nombreEstado}
                                                                    onClick={() => handleCambiarEstado(pub, nombreEstado)}
                                                                    disabled={pub.estado === nombreEstado}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '0.5rem'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={config.icon}
                                                                        style={{ color: config.color, width: '20px' }}
                                                                    />
                                                                    {nombreEstado}
                                                                    {pub.estado === nombreEstado && (
                                                                        <FontAwesomeIcon icon="check" className="ms-auto text-success" />
                                                                    )}
                                                                </Dropdown.Item>
                                                            ))}
                                                        </Dropdown.Menu>
                                                    </Dropdown>



                                                    {pub.nombreUsuario && (
                                                        <>
                                                            <FontAwesomeIcon icon="user" className="me-1" />
                                                            {pub.nombreUsuario}
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Información de fecha destacada */}
                                            <div className="mb-3" style={{
                                                backgroundColor: isDark ? '#1a1d20' : '#f8f9fa',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                border: `2px solid ${isDark ? '#343a40' : '#e9ecef'}`
                                            }}>
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#0d6efd',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FontAwesomeIcon icon="calendar-alt" style={{ color: 'white', fontSize: '1.1rem' }} />
                                                        </div>
                                                        <div>
                                                            <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                Fecha de Publicaci&oacute;n
                                                            </small>
                                                            <div className="fw-bold" style={{ fontSize: '0.95rem', color: isDark ? '#e9ecef' : '#212529' }}>
                                                                {dayjs(fechaEvento).format('dddd, DD [de] MMMM [de] YYYY')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {!estaEditandoFecha && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => iniciarEdicionFecha(pub)}
                                                            disabled={estaActualizandoFecha}
                                                            style={{ borderRadius: '6px', fontWeight: '500' }}
                                                        >
                                                            <FontAwesomeIcon icon="edit" className="me-1" />
                                                            Cambiar
                                                        </Button>
                                                    )}
                                                </div>

                                                {estaEditandoFecha && (
                                                    <div className="mt-3 pt-3 border-top">
                                                        <Form.Group>
                                                            <Form.Label className="small mb-2 fw-semibold">Nueva fecha:</Form.Label>
                                                            <div className="d-flex gap-2">
                                                                <Form.Control
                                                                    type="date"
                                                                    size="sm"
                                                                    value={nuevaFecha}
                                                                    onChange={(e) => setNuevaFecha(e.target.value)}
                                                                    disabled={estaActualizandoFecha}
                                                                    style={{ borderRadius: '6px' }}
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    variant="success"
                                                                    onClick={() => handleCambiarFecha(pub)}
                                                                    disabled={estaActualizandoFecha || !nuevaFecha}
                                                                    style={{ borderRadius: '6px', minWidth: '38px' }}
                                                                >
                                                                    {estaActualizandoFecha ? (
                                                                        <span className="spinner-border spinner-border-sm" />
                                                                    ) : (
                                                                        <FontAwesomeIcon icon="check" />
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    onClick={cancelarEdicionFecha}
                                                                    disabled={estaActualizandoFecha}
                                                                    style={{ borderRadius: '6px', minWidth: '38px' }}
                                                                >
                                                                    <FontAwesomeIcon icon="times" />
                                                                </Button>
                                                            </div>
                                                        </Form.Group>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Tema */}
                                            {pub.tema && (
                                                <div className="mb-3">
                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                        <div style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? '#495057' : '#e9ecef',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FontAwesomeIcon icon="tag" style={{ color: isDark ? '#adb5bd' : '#6c757d', fontSize: '0.9rem' }} />
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex align-items-center justify-content-between mb-1">
                                                                <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Tema
                                                                </small>
                                                                {!estaEditandoTema && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-primary"
                                                                        onClick={() => iniciarEdicionTema(pub)}
                                                                        disabled={estaActualizandoTema}
                                                                        style={{ borderRadius: '6px', fontWeight: '500', padding: '0.2rem 0.5rem' }}
                                                                    >
                                                                        <FontAwesomeIcon icon="edit" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                            {estaEditandoTema ? (
                                                                <div>
                                                                    <Form.Control
                                                                        type="text"
                                                                        value={nuevoTema}
                                                                        onChange={(e) => setNuevoTema(e.target.value)}
                                                                        disabled={estaActualizandoTema}
                                                                        style={{ borderRadius: '6px', fontSize: '0.9rem' }}
                                                                        maxLength={255}
                                                                    />
                                                                    <div className="d-flex gap-2 mt-2">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="success"
                                                                            onClick={() => handleCambiarTema(pub)}
                                                                            disabled={estaActualizandoTema || !nuevoTema.trim()}
                                                                            style={{ borderRadius: '6px' }}
                                                                        >
                                                                            {estaActualizandoTema ? (
                                                                                <span className="spinner-border spinner-border-sm" />
                                                                            ) : (
                                                                                <FontAwesomeIcon icon="check" />
                                                                            )}
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="secondary"
                                                                            onClick={cancelarEdicionTema}
                                                                            disabled={estaActualizandoTema}
                                                                            style={{ borderRadius: '6px' }}
                                                                        >
                                                                            <FontAwesomeIcon icon="times" />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <p className="mb-0 fw-semibold" style={{ fontSize: '0.9rem', color: isDark ? '#e9ecef' : '#212529' }}>
                                                                    {pub.tema}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Descripción */}
                                            <div className="mb-3">
                                                <div className="d-flex align-items-start gap-2">
                                                    <div style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '6px',
                                                        backgroundColor: isDark ? '#495057' : '#e9ecef',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0
                                                    }}>
                                                        <FontAwesomeIcon icon="align-left" style={{ color: isDark ? '#adb5bd' : '#6c757d', fontSize: '0.9rem' }} />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                                            <div>
                                                                <small className="text-muted d-block" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Descripci&oacute;n
                                                                </small>
                                                                {/* Nueva etiqueta para mostrar si está siendo editado */}
                                                                {estaEditandoDescripcion && (
                                                                    <span className="badge bg-warning text-dark rounded-pill" style={{ fontSize: '0.7rem' }}>
                                                                        Editando
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {!estaEditandoDescripcion && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-primary"
                                                                    onClick={() => iniciarEdicionDescripcion(pub)}
                                                                    disabled={estaActualizandoDescripcion}
                                                                    style={{ borderRadius: '6px', fontWeight: '500', padding: '0.2rem 0.5rem' }}
                                                                >
                                                                    <FontAwesomeIcon icon="edit" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                        {estaEditandoDescripcion ? (
                                                            <div>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    value={nuevaDescripcion}
                                                                    onChange={(e) => setNuevaDescripcion(e.target.value)}
                                                                    disabled={estaActualizandoDescripcion}
                                                                    style={{ borderRadius: '6px', fontSize: '0.9rem' }}
                                                                    maxLength={100}
                                                                />
                                                                <div className="d-flex gap-2 mt-2">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="success"
                                                                        onClick={() => handleCambiarDescripcion(pub)}
                                                                        disabled={estaActualizandoDescripcion || !nuevaDescripcion.trim()}
                                                                        style={{ borderRadius: '6px' }}
                                                                    >
                                                                        {estaActualizandoDescripcion ? (
                                                                            <span className="spinner-border spinner-border-sm" />
                                                                        ) : (
                                                                            <FontAwesomeIcon icon="check" />
                                                                        )}
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="secondary"
                                                                        onClick={cancelarEdicionDescripcion}
                                                                        disabled={estaActualizandoDescripcion}
                                                                        style={{ borderRadius: '6px' }}
                                                                    >
                                                                        <FontAwesomeIcon icon="times" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <p className="mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.5', color: isDark ? '#dee2e6' : '#495057' }}>
                                                                {pub.descripcion}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Fila con Formato y Llamada de Acción */}
                                            <div className="row mb-3 g-3">
                                                {/* Formato - 50% */}
                                                {pub.formato && (
                                                    <div className="col-md-6">
                                                        <FormatoEditable
                                                            publicacion={pub}
                                                            formato={pub.formato}
                                                            catalogoFormatos={catalogoFormatos}
                                                            estaEditando={estaEditandoFormato}
                                                            estaActualizando={estaActualizandoFormato}
                                                            nuevoValor={nuevoFormato}
                                                            setNuevoValor={setNuevoFormato}
                                                            onIniciarEdicion={iniciarEdicionFormato}
                                                            onCancelarEdicion={cancelarEdicionFormato}
                                                            onGuardar={handleCambiarFormato}
                                                            isDark={isDark}
                                                        />
                                                    </div>
                                                )}

                                                {/* Llamada de Acción - 50% */}
                                                {pub.llamadaAccion && (
                                                    <div className={pub.formato ? "col-md-6" : "col-12"}>
                                                        <LlamadaAccionEditable
                                                            publicacion={pub}
                                                            llamadaAccion={pub.llamadaAccion}
                                                            catalogoLlamadasAccion={catalogoLlamadasAccion}
                                                            estaEditando={estaEditandoLlamadaAccion}
                                                            estaActualizando={estaActualizandoLlamadaAccion}
                                                            nuevoValor={nuevaLlamadaAccion}
                                                            setNuevoValor={setNuevaLlamadaAccion}
                                                            onIniciarEdicion={iniciarEdicionLlamadaAccion}
                                                            onCancelarEdicion={cancelarEdicionLlamadaAccion}
                                                            onGuardar={handleCambiarLlamadaAccion}
                                                            isDark={isDark}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {/* Redes Sociales */}
                                            <div>
                                                <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Redes Sociales
                                                </small>
                                                <div className="d-flex gap-2 flex-wrap">
                                                    {redes.map((redId) => {
                                                        const redInfo = redesSocialesInfo[redId];
                                                        return redInfo ? (
                                                            <div
                                                                key={redId}
                                                                style={{
                                                                    backgroundColor: '#009bdb',
                                                                    borderRadius: '8px',
                                                                    padding: '8px 12px',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '6px',
                                                                    minWidth: '36px',
                                                                    height: '36px',
                                                                    boxShadow: '0 2px 4px rgba(0,155,219,0.3)',
                                                                    transition: 'all 0.2s',
                                                                    cursor: 'default'
                                                                }}
                                                                title={redInfo.nombre}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.transform = 'scale(1.05)';
                                                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,155,219,0.4)';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.transform = 'scale(1)';
                                                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,155,219,0.3)';
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={redInfo.icon}
                                                                    style={{
                                                                        fontSize: '1rem',
                                                                        color: 'white'
                                                                    }}
                                                                />
                                                                <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
                                                                    {redInfo.nombre}
                                                                </span>
                                                            </div>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sección de historial */}
                                        <Accordion className="mb-3">
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div style={{
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '6px',
                                                            backgroundColor: isDark ? '#495057' : '#e9ecef',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <FontAwesomeIcon icon="history" style={{ color: isDark ? '#adb5bd' : '#6c757d' }} />
                                                        </div>
                                                        <span className="fw-semibold">Historial de cambios ({historial.length})</span>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body style={{ backgroundColor: isDark ? '#1a1d20' : '#ffffff', padding: '1.25rem' }}>
                                                    {cargandoHistorial ? (
                                                        <div className="text-center py-3">
                                                            <span className="spinner-border spinner-border-sm me-2" />
                                                            <small>Cargando historial...</small>
                                                        </div>
                                                    ) : historial.length > 0 ? (
                                                        <div className="timeline">
                                                            {historial.map((evento, idx) => {
                                                                const tipoEvento = evento.vTipoEvento || evento.tipo;
                                                                const fechaEvento = evento.dFechaEvento || evento.fecha;
                                                                const descripcion = evento.vDescripcion || evento.descripcion;
                                                                const usuario = evento.vUsuario || evento.usuario;

                                                                let iconEvento = 'circle';
                                                                let colorEvento = '#6c757d';

                                                                if (tipoEvento === 'Cambio de Estado') {
                                                                    iconEvento = 'exchange-alt';
                                                                    colorEvento = '#009bdb';
                                                                } else if (tipoEvento === 'Cambio de Fecha') {
                                                                    iconEvento = 'calendar-alt';
                                                                    colorEvento = '#fc9646';
                                                                }

                                                                return (
                                                                    <div key={idx} className="d-flex gap-3 mb-3">
                                                                        <div style={{ minWidth: '30px' }}>
                                                                            <div
                                                                                style={{
                                                                                    width: '30px',
                                                                                    height: '30px',
                                                                                    borderRadius: '50%',
                                                                                    backgroundColor: colorEvento,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    icon={iconEvento}
                                                                                    style={{ fontSize: '0.7rem', color: 'white' }}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-grow-1">
                                                                            <div className="d-flex justify-content-between align-items-start">
                                                                                <div>
                                                                                    <p className="mb-0 small fw-semibold">{tipoEvento}</p>
                                                                                    <p className="mb-0 small text-muted">{descripcion}</p>
                                                                                </div>
                                                                                <small className="text-muted">
                                                                                    {dayjs(fechaEvento).format('DD/MM/YYYY HH:mm')}
                                                                                </small>
                                                                            </div>
                                                                            {usuario && (
                                                                                <small className="text-muted">
                                                                                    <FontAwesomeIcon icon="user" className="me-1" />
                                                                                    {usuario}
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-3 text-muted">
                                                            <FontAwesomeIcon icon="info-circle" className="me-2" />
                                                            <small>No hay cambios registrados</small>
                                                        </div>
                                                    )}
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <FontAwesomeIcon icon="calendar-xmark" size="3x" className="text-muted mb-3" />
                            <p className="text-muted">No hay publicaciones programadas para este día</p>
                        </div>
                    )}
                </Modal.Body>
                                                              
                <Modal.Footer
                    className="border-top-0 p-3"
                    style={{
                        backgroundColor: isDark ? '#1a1d20' : '#f8f9fa',
                        borderTop: `2px solid ${isDark ? '#343a40' : '#e9ecef'}`
                    }}
                >
                    <Button
                        variant="light"
                        size="sm"
                        onClick={handleClose}
                        style={{
                            borderRadius: '6px',
                            padding: '0.5rem 1.5rem',
                            fontWeight: '500',
                            backgroundColor: isDark ? '#495057' : '#e9ecef',
                            border: 'none',
                            color: isDark ? '#e9ecef' : '#495057'
                        }}
                    >
                        <FontAwesomeIcon icon="times" className="me-2" />
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

PublicacionEventModal.propTypes = {
    setIsOpenEventModal: PropTypes.func.isRequired,
    isOpenEventModal: PropTypes.bool.isRequired,
    modalEventContent: PropTypes.object.isRequired,
    onEstadoActualizado: PropTypes.func,
    selectedCuentaId: PropTypes.number
};

export default PublicacionEventModal;
